import { notification } from '@/components/tdesign';
import { intlGet, intlGetApiError } from '@/locales';
import { v4 as uuidv4 } from 'uuid';
// api
import { segmentsManageApi } from '@/api';
import { handleReturnResults } from '@/api/interfaceUtils';
// hooks
// utils
import { getSourceText } from '@/pages/contactManage/details/varAndFun';
import {
  doesItExist,
  isArray,
  isArrayLen,
  isEmptyObject,
  isString,
} from '@/utils/common/dataJudgment';
import { convertKeysToUnderscore } from '@/utils/format/letter';
import { compareNum } from '@/utils/format/number';
import { onPattern, pattern } from '@/utils/regular';
// type
import type { ConditionsListIF } from '@/api/segmentsManage/data.d';
import { FieldTypeEnum, GroupTypeEnum } from '@/api/segmentsManage/data.d';
import { DEFAULT_LANGUAGE } from '@/config/defaultConfig';
import { StoreKeyEnum } from '@/definition/common/data.d';
import type { TdFormRule } from '@/definition/common/tdesign.d';
import type { JourneyNodesContentRatingFilters } from '@/pages/userJourney/utils/typing/create';
import { onJoinData, onSplitData } from '@/utils/common';
import {
  onInterfaceTime,
  onInterfaceTimeNew,
} from '@/utils/common/businessLogic';
import {
  getEnumLangValue,
  getKeyValuePairs,
} from '@/utils/common/dataConversion';
import { getTimeStamp } from '@/utils/format/time';
import { getLocalStorage, jsonParse, jsonStringify } from '@/utils/global';
import type { ReactNode } from 'react';
import { ContentRatingTabEnum } from '../enum/addOrEdit';
import type {
  CampaignScopeFormData,
  ContentRatingFormData,
  ExtendDataItemIF,
  FilterFormType,
  FilterOptionType,
  FormDataType,
} from '../typings/addOrEdit';
import {
  ContactEngagementFilterEnum,
  ContactEngagementOperatorTypeEnum,
  ContactEngagementRadioEnum,
  HandleDataType,
  RangeDateEnum,
  UserJourneyFilterEnum,
  WhatsAppActivityFilterEnum,
} from '../typings/addOrEdit';

// 需要开启下拉多选的key
export const multipleSelectKey = [
  'custom_field_language',
  'custom_field_country',
  'preference',
  'tag',
  'source',
  'from_api',
  'tester_user_status',
];

/** 分层名称验证重复 */
export const segmentsNameValidator = async ({
  tag,
  id = 0,
  getCheckFlag,
  callBack,
}: {
  tag: string;
  id?: number;
  getCheckFlag: (result: any) => boolean;
  callBack?: (val: boolean) => void;
}) => {
  let returnParams = {
    result: true,
    message: '',
    type: 'success',
    trigger: '',
  };
  const result = await segmentsManageApi.checkName({
    segmentId: id,
    segmentTitle: tag?.trim(),
  });
  const flag = getCheckFlag(result);
  if (flag) {
    returnParams = {
      result: false,
      message: intlGetApiError('4002'),
      type: 'error',
      trigger: 'blur',
    };
  }
  callBack?.(returnParams.result);
  return returnParams;
};

/** 分层名称 - 校验规则 */
export const segmentsNameRule = (params?: {
  id?: number;
  callBack?: (val: boolean) => void;
}): TdFormRule[] => [
  {
    required: true,
    messageKey: 'SEGMENTS.MANAGE.LIST.CREATE.ENTER.SEGMENTS.NAME',
    type: 'error',
  },
  {
    whitespace: true,
    messageKey: 'SEGMENTS.MANAGE.LIST.CREATE.ENTER.SEGMENTS.NAME',
    type: 'error',
  },
  {
    validator: (tag: string) =>
      segmentsNameValidator({
        ...params,
        tag,
        getCheckFlag: (res: any) => res.data?.exist,
      }),
  },
];

// 是否选择等于（=）
export const isSelectedEqual = (operator?: string) => operator === '=';
// 是否选择减号（-）
export const isSelectedMinusSign = (operator: string) => operator === '-';
// 是否区间值（range、!=）
export const isIntervalValue = (operator: string | undefined) =>
  ['range', '!='].some((v) => v == operator);
// 是否时间选择
export const isTimeSelect = (fieldType: string) =>
  fieldType === FieldTypeEnum.DATE || fieldType === FieldTypeEnum.DATETIME;

// 是否是来源
export const isSource = (key: string) => key === 'source';
// 映射来源名称
export const getSourceName = (sourceKey: string, nameJson: any) => {
  let text = getSourceText(sourceKey);

  // 当表单类型时
  if (doesItExist('API-', sourceKey) || doesItExist('form-', sourceKey)) {
    text = nameJson?.label;
  }
  return text;
};
// 来源表单校验
export const onVerificationSource = (
  extendData: any,
  optionsJson: any,
  validateOnly?: boolean,
) => {
  let flag = true;
  for (const exKey in extendData) {
    const obj: any = extendData[exKey];
    let isBreak = false;
    for (const apiKey in obj) {
      const extendItem: ExtendDataItemIF = obj?.[apiKey];
      const dateOption = extendItem?.dateOption;
      const startTime = extendItem?.startTime;
      const endTime = extendItem?.endTime;
      // 距离现在
      if (dateOption === 4) {
        // 错误提示信息
        let descriptionAfter = '';
        // 全无值
        const notVal =
          !extendItem?.subOperator &&
          !extendItem?.leftValue &&
          !extendItem?.rightValue &&
          !extendItem.value;
        // 无值
        const isNoValue = isIntervalValue(extendItem?.subOperator)
          ? !extendItem?.leftValue || !extendItem?.rightValue
          : !extendItem.value;
        // 值非数字
        const isNotNumber = isIntervalValue(extendItem?.subOperator)
          ? !onPattern(extendItem?.leftValue, pattern.isInt) ||
            !onPattern(extendItem?.rightValue, pattern.isInt)
          : !onPattern(extendItem?.value, pattern.isInt);

        // 距离现在: 无值
        if (notVal) {
          flag = false;
          isBreak = true;
          // 请输入距离现在
          descriptionAfter = intlGet(
            'SEGMENTS.MANAGE.TIME.ENTER.DAYS.UNTIL.NOW',
          );
        } else if (isNoValue) {
          flag = false;
          isBreak = true;
          // 天数不能为空
          descriptionAfter = intlGet('SEGMENTS.MANAGE.FILTER.DAYS.NOT.NULL');
        } else if (isNotNumber) {
          flag = false;
          isBreak = true;
          // 请输入数字
          descriptionAfter = intlGet('SEGMENTS.MANAGE.FILTER.ENTER.NUMBER');
        } else if (
          isIntervalValue(extendItem?.subOperator) &&
          compareNum(extendItem?.rightValue || 0, extendItem?.leftValue || 0) ==
            -1
        ) {
          flag = false;
          isBreak = true;
          // 填入的最大值大于等于其最小值
          descriptionAfter = intlGet('SEGMENTS.MANAGE.FILTER.MAX.GTE.MIN');
        }
        !validateOnly &&
          descriptionAfter &&
          notification.error({
            description: `${getSourceName(
              exKey,
              optionsJson[exKey],
            )}---${descriptionAfter}`,
          });
      } else if (dateOption === 3) {
        // 开始时间应小于结束时间
        if (!startTime || !endTime) {
          flag = false;
          isBreak = true;
          !validateOnly &&
            notification.error({
              description: `${getSourceName(
                exKey,
                optionsJson[exKey],
              )}---${intlGet('SEGMENTS.MANAGE.SELECT.TIME')}`,
            });
        } else if (getTimeStamp(startTime) > getTimeStamp(endTime)) {
          flag = false;
          isBreak = true;
          !validateOnly &&
            notification.error({
              description: `${getSourceName(
                exKey,
                optionsJson[exKey],
              )}---${intlGet('SEGMENTS.MANAGE.TIME.FROM.TO.LE')}`,
            });
        }
      } else if (!extendItem.value) {
        !validateOnly &&
          notification.error({
            description: `${getSourceName(
              exKey,
              optionsJson[exKey],
            )}---${intlGet('SEGMENTS.MANAGE.SELECT.TIME')}`,
          });
        flag = false;
        isBreak = true;
      }
    }
    if (isBreak) break;
  }

  return flag;
};

// 标识是'有关您联系人的个人详细信息'内需要增加null选项
export const isIncludeNullValueExtra = ({
  datasource,
  key,
  group,
}: {
  datasource: string;
  key: string;
  group: string;
}) =>
  ((datasource === 'user_extra' || key === 'preference') &&
    group === 'customField') ||
  key === UserJourneyFilterEnum.CONTENT_RATING;
// 临时隐藏偏好和dropDown类型自定义字段是否为空的选项
export const temporarilyHiddenNullCondition = (
  filterConfig: ConditionsListIF,
) => {
  // dropDown字段
  const isDropDown = filterConfig?.fieldType === 'drop down';
  // 偏好
  const isPrefernece = filterConfig?.key === 'preference';
  // 字段管理分组
  const isCustomField = filterConfig?.group === 'customField';
  return isCustomField && (isDropDown || isPrefernece);
};

// 标识是Number类型自定义字段
export const isIntExtra = ({
  datasource,
  fieldType,
}: {
  datasource: string;
  fieldType: string;
}) => {
  return datasource === 'user_extra' && fieldType === FieldTypeEnum.INT;
};

// 标识是DropDown类型、偏好，将控制合并到下拉项中
export const isDropDownExtra = ({
  datasource,
  fieldType,
  key,
}: {
  datasource: string;
  fieldType: string;
  key: string;
}) => {
  // dropDown类型自定义字段
  const isDropDownField =
    datasource === 'user_extra' && fieldType === FieldTypeEnum.DROP_DOWN;
  // 偏好
  const preference = key === 'preference';
  return isDropDownField || preference;
};

// 是否需要开放 选择活动时间范围或者活动 - 一类
export const isSelectActivityOne = (
  key: ContactEngagementFilterEnum | string,
) => {
  const openArr: any[] = [
    ContactEngagementFilterEnum.CLICKED,
    ContactEngagementFilterEnum.OPENED,
    WhatsAppActivityFilterEnum.CLICKED,
  ];
  return openArr.includes(key);
};
// 是否需要开放 选择活动时间范围或者活动 - 二类
export const isSelectActivityTwo = (
  key: ContactEngagementFilterEnum | string,
) => {
  const openArr: any[] = [
    ContactEngagementFilterEnum.SEND,
    ContactEngagementFilterEnum.NOT_CLICKED,
    ContactEngagementFilterEnum.NOT_OPENED,
    ContactEngagementFilterEnum.NOT_SEND,
    WhatsAppActivityFilterEnum.NOT_SENT,
    WhatsAppActivityFilterEnum.NOT_READ,
    WhatsAppActivityFilterEnum.SENT,
    WhatsAppActivityFilterEnum.READ,
    WhatsAppActivityFilterEnum.REPLIED,
  ];
  return openArr.includes(key);
};

// 是否需要开放 选择活动或选择活动及旅程
export const isSelectActivityAndJourney = (key: string) =>
  UserJourneyFilterEnum.CONTENT_RATING === key;

// 测试玩家定制条件: 测试状态
export const isSelectTesterStatus = (item) =>
  'tester_user_status' === item.key &&
  item.group === GroupTypeEnum.TESTER_STATUS;

// 处理联系人参与度提交数据
export const handleSubmitCampaignScope = (
  options: any[],
  params?: CampaignScopeFormData,
  isWhatsAppCampaign?: boolean,
) => {
  const obj = getKeyValuePairs({
    list: options,
    key: 'value',
    value: 'type',
  });
  const { timeRangeCampaignIds, ...baseParams } = params || {};
  const newParams = {
    ...baseParams,
  };
  const type = params?.type || ContactEngagementRadioEnum.TIME_RANGE;
  switch (type) {
    case ContactEngagementRadioEnum.TIME_RANGE:
      if (newParams?.timeRange) {
        newParams.type = obj?.[newParams?.timeRange];
        if (!isWhatsAppCampaign) {
          newParams.campaignId = [];
        } else {
          // WhatsApp 活动使用时间范围对应的活动ID
          newParams.campaignId = timeRangeCampaignIds || [];
        }
        newParams.campaignName = '';
      }
      break;
    case ContactEngagementRadioEnum.CAMPAIGNS:
      newParams.type = 'campaign';
      newParams.campaignName = jsonStringify(newParams.campaignName);
      newParams.timeRange = '';
      break;
  }
  return newParams;
};

// 处理满意度评价提交数据
export const handleSubmitContentRatinge = (params?: ContentRatingFormData) => {
  const newParams: any = {
    ...params,
  };
  const type = params?.type;
  switch (type) {
    case ContentRatingTabEnum.JOURNEY:
      newParams.campaignId = 0;
      newParams.campaignName = '';
      break;
    case ContentRatingTabEnum.CAMPAIGN:
      newParams.campaignName = jsonStringify(newParams.campaignName);
      newParams.journeyId = '';
      break;
  }
  return newParams;
};

// 处理联系人参与度详情数据
export const handleDetailsCampaignScope = (
  params: CampaignScopeFormData,
  onlyKey?: string,
) => {
  let newParams: any = {
    ...params,
  };
  const type =
    params.type === 'campaign'
      ? ContactEngagementRadioEnum.CAMPAIGNS
      : ContactEngagementRadioEnum.TIME_RANGE;
  // 历史数据前端兼容显示 - 未点击、未打开历史结果无此字段，前端默认选中'none'
  if (onlyKey && !params?.operatorType) {
    let operatorType = '';
    if (isSelectActivityTwo(onlyKey)) {
      operatorType = ContactEngagementOperatorTypeEnum.NONE;
    }

    operatorType && (newParams = { ...newParams, operatorType });
  }

  return {
    ...newParams,
    campaignName: jsonParse(params.campaignName),
    type,
  };
};

export const onHandleJourneyNodesContentRatingFilters = (
  data: JourneyNodesContentRatingFilters,
  {
    disabled,
    optType,
    currentTimezone,
    getEnumOptions,
  }: {
    disabled?: boolean;
    optType: HandleDataType;
    currentTimezone: string;
    getEnumOptions?: (item: any, key: string) => any;
  },
): FilterFormType => {
  const {
    actionType,
    isIncludeNullValue,
    order,
    rating,
    campaignId,
    campaignName,
    nodeUuids,
    type,
  } = data;
  let tab = type;
  if (!tab) {
    tab = isArrayLen(nodeUuids)
      ? ContentRatingTabEnum.JOURNEY
      : ContentRatingTabEnum.CAMPAIGN;
  }
  let defaultForm = {
    key: UserJourneyFilterEnum.CONTENT_RATING,
    disabled,
    errorArr: [],
    errorMsg: '',
    fieldType: 'enum',
    fieldName: UserJourneyFilterEnum.CONTENT_RATING,
    formItemHide: false,
    group: GroupTypeEnum.USER_JOURNEY_GROUP,
    datasource: '',
    operator: '',
    subOperator: '',
    timezone: currentTimezone,
    isIncludeNullValue,
    value: rating.map((val) => val?.toString()),
    order,
  } as Partial<FilterFormType> & { order: number };

  switch (tab) {
    case ContentRatingTabEnum.CAMPAIGN:
      const campaignid = Number(campaignId?.pop());
      defaultForm = {
        ...defaultForm,
        contentRating: {
          type: tab,
          journeyId: '',
          campaignId: campaignid,
          campaignName: [
            {
              label: campaignName,
              value: campaignid,
            },
          ] as { label: string; value: number }[],
        },
      } as FilterFormType & { order: number };
      break;
    case ContentRatingTabEnum.JOURNEY:
    default:
      defaultForm = {
        ...defaultForm,
        contentRating: {
          type: tab,
          journeyId: nodeUuids?.pop(),
          campaignName: '',
          campaignId: 0,
        },
      } as FilterFormType & { order: number };
      break;
  }
  return defaultForm as FilterFormType;
};

// 获取枚举筛选条件
export const getFiltersEnumOptions = ({
  data,
  fieldKey,
  filterJson,
  mapCallBack,
}: {
  /** 待处理信息 */
  data: FilterFormType;
  /** 当前条件配置项options 的 Key */
  fieldKey: string;
  /** 分层条件配置信息 */
  filterJson: any;
  /** 遍历枚举信息自定义回调 */
  mapCallBack?: (data: any) => any;
}) => {
  const localLanguage =
    getLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE) || DEFAULT_LANGUAGE;
  const currentItem: any = filterJson?.[data.key];
  const list: {
    text: string;
    value: any;
    groupKey?: string;
    // 是否预测标签
    isBuiltin?: boolean;
  }[] = (currentItem?.[fieldKey] || []).map((item: any) => {
    let { value } = item;
    // 来源数据兼容
    if (isSource(currentItem.key) && fieldKey === 'filterOption') {
      value = item.key;
    }
    return mapCallBack
      ? mapCallBack(item)
      : {
          ...item,
          // 组件兼容所以写了text 和 label
          text: getEnumLangValue(item.text, localLanguage),
          label: getEnumLangValue(item.text, localLanguage),
          value,
          groupKey: item.group,
          isBuiltin: item?.isBuiltin,
        };
  });
  return list;
};

// 处理分层条件数据格式
export const onHandleFilters = (
  data: FilterFormType[],
  {
    disabled,
    optType,
    currentTimezone,
    filterJson,
    getEnumOptions,
  }: {
    disabled?: boolean;
    optType: HandleDataType;
    currentTimezone: string;
    filterJson?: any;
    getEnumOptions?: (item: any, key: string) => any;
  },
) => {
  const timezoneNum = currentTimezone?.split('UTC')[1];
  return data.map((item) => {
    const newItem = { ...item, disabled };
    if (!newItem?.uuid) {
      newItem.uuid = uuidv4();
    }
    let isMultiple = false;
    const isMultipleNumber = false;
    // 当类型为自定义字段下拉、白名单配置
    if (
      item.fieldType === FieldTypeEnum.DROP_DOWN ||
      multipleSelectKey.includes(item.key) ||
      isSelectActivityAndJourney(item.key)
    ) {
      isMultiple = true;
    }

    // 处理多选的情况
    if (isMultiple) {
      let value: any = '';
      if (optType === HandleDataType.SUBMIT) {
        value = isArray(item.value) ? onJoinData(item.value) : item.value;
      } else {
        value = isArray(item.value) ? item.value : onSplitData(item.value);
        // 当多选需要转成数字时
        if (isMultipleNumber) {
          value = value.map(Number);
        }
      }

      // 兼容老数据问题 - 20230817
      if (item.key === 'tag' || isSource(item.key)) {
        if (item.operator === '!=') {
          newItem.operator = 'notInAll';
        }
        if (item.operator === '=') {
          newItem.operator = 'inAll';
        }
      }
      // 兼容老数据问题 - 20230913
      if (item.key === 'from_api' || item.key === 'from_file_upload') {
        newItem.key = 'source';
      }
      // 当有值被删除，则提示
      // if (item.valueDeleted && optType === HandleDataType.DETAILS) {
      //   form.setValidateMessage({
      //     "value": [{
      //       type: 'error',
      //       message: intlGet('SEGMENTS.MANAGE.FILTER.VALUE.DEL.TIPS'),
      //     }]
      //   })
      // }

      /**
       * 来源（详情、提交） - 时间转换
       * 时间转换：编辑详情信息时提交的时间转为详情获取的时区时间到接口入参
       * 时间转换：编辑态且新增筛选提交时，提交的时区信息才是当前sml设置的时区时间
       *  */
      if (
        newItem.key === 'source' &&
        (optType === HandleDataType.DETAILS ||
          optType === HandleDataType.SUBMIT)
      ) {
        const extendData = { ...item.extendData };
        for (const exKey in extendData) {
          const obj: any = extendData[exKey];
          for (const apiKey in obj) {
            // 提交及详情时区时间转换参数
            let switchTimeOpts = {};
            const dataTimezone = obj[apiKey].timezone;

            if (dataTimezone) {
              switchTimeOpts =
                optType === HandleDataType.SUBMIT
                  ? {
                      optsInputTimeZone: currentTimezone,
                      optsTargetTimeZone: dataTimezone,
                    }
                  : { optsInputTimeZone: dataTimezone };
            }

            const extendItem: ExtendDataItemIF = obj?.[apiKey];
            if (apiKey === 'joinTime') {
              // 距离现在
              const isDaysUntilNow = obj[apiKey].dateOption === 4;
              const subOperatorEq = obj[apiKey].subOperator === '=';
              if (obj[apiKey].startTime) {
                obj[apiKey].startTime = onInterfaceTime(
                  extendItem?.startTime,
                  switchTimeOpts,
                );
              }
              if (obj[apiKey].endTime) {
                obj[apiKey].endTime = onInterfaceTime(
                  extendItem?.endTime,
                  switchTimeOpts,
                );
              }
              if (obj[apiKey].value && !isDaysUntilNow) {
                obj[apiKey].value = onInterfaceTime(
                  extendItem?.value,
                  switchTimeOpts,
                );
              }
              // 提交非距离现在项信息，则距离现在相关信息清空
              if (!isDaysUntilNow) {
                obj[apiKey] = {
                  ...obj[apiKey],
                  leftValue: '',
                  rightValue: '',
                  subOperator: '',
                };
              }
              if (isDaysUntilNow && subOperatorEq) {
                obj[apiKey] = {
                  ...obj[apiKey],
                  leftValue: '',
                  rightValue: '',
                };
              }
              if (isDaysUntilNow && !subOperatorEq) {
                obj[apiKey] = {
                  ...obj[apiKey],
                  value: '',
                };
              }
            }
          }
        }
        newItem.extendData = extendData;
      }

      /** 满意度评价 */
      if (
        optType === HandleDataType.SUBMIT &&
        isSelectActivityAndJourney(newItem.key)
      ) {
        const newCampaignScope: any = handleSubmitContentRatinge(
          newItem.contentRating,
        );
        const campaignId = newCampaignScope.campaignId
          ? [newCampaignScope.campaignId]
          : [];
        // 满意度评价使用campaignScope字段传递结果给提交接口
        newItem.campaignScope = {
          ...newCampaignScope,
          campaignId,
        };
      }

      /** 满意度评价 使用campaignScope字段接收详情获取的满意度信息 */
      if (
        newItem.campaignScope &&
        isSelectActivityAndJourney(newItem.key) &&
        optType === HandleDataType.DETAILS
      ) {
        const campaignId = newItem.campaignScope.campaignId[0] || 0;
        value = (value || []).sort(
          (a: string, b: string) => Number(a) - Number(b),
        );
        newItem.contentRating = {
          ...newItem.campaignScope,
          campaignId,
          campaignName: jsonParse(newItem.campaignScope.campaignName) || [],
        };
      }

      return {
        ...newItem,
        value,
      };
    }

    // 详情时
    if (optType === HandleDataType.DETAILS) {
      // 时间转换
      if (
        newItem.fieldType === FieldTypeEnum.DATE ||
        newItem.fieldType === FieldTypeEnum.DATETIME
      ) {
        // 提交及详情时区时间转换参数
        const switchTimeOpts = {
          optsInputTimeZone: item.timezone || currentTimezone,
        };

        if (item.value) {
          newItem.value = onInterfaceTime(item.value, switchTimeOpts);
        }
        if (item.startTime) {
          newItem.startTime = onInterfaceTime(item.startTime, switchTimeOpts);
        }
        if (item.endTime) {
          newItem.endTime = onInterfaceTime(item.endTime, switchTimeOpts);
        }

        // 距离现在
        if (isIntervalValue(newItem?.subOperator)) {
          newItem.dateValue = undefined;
        }
      }
      /** 联系人参与度 */
      if (
        newItem.campaignScope &&
        (isSelectActivityOne(newItem.key) || isSelectActivityTwo(newItem.key))
      ) {
        newItem.campaignScope = handleDetailsCampaignScope(
          newItem.campaignScope,
          newItem.key,
        );
      }
    }
    /**
     * 当提交时
     * 时间转换：编辑详情信息时提交的时间转为详情获取的时区时间到接口入参
     * 时间转换：编辑态且新增筛选提交时，提交的时区信息才是当前sml设置的时区时间
     *  */
    if (optType === HandleDataType.SUBMIT) {
      // 时间选择范围
      if (newItem.dateOption === RangeDateEnum.OPTIONS1) {
        newItem.startTime = '';
        newItem.endTime = '';
      }
      if (newItem.dateOption === RangeDateEnum.OPTIONS3) {
        newItem.dateValue = 0;
      }
      // 当类型为日期选择时
      if (isTimeSelect(newItem.fieldType)) {
        newItem.timezone = timezoneNum;
        if (newItem.value) {
          newItem.value = onInterfaceTimeNew(newItem.value, {
            optsInputTimeZone: timezoneNum,
          });
        }
        if (newItem.startTime) {
          newItem.startTime = onInterfaceTimeNew(newItem.startTime, {
            optsInputTimeZone: timezoneNum,
          });
        }
        if (newItem.startTime) {
          newItem.endTime = onInterfaceTimeNew(newItem.endTime, {
            optsInputTimeZone: timezoneNum,
          });
        }
        if (isSelectedEqual(newItem.operator)) {
          newItem.value = '';
        } else {
          newItem.startTime = '';
          newItem.endTime = '';
        }

        // 距离现在
        if (isSelectedEqual(newItem?.subOperator)) {
          newItem.value = '';
          newItem.leftValue = '';
          newItem.rightValue = '';
        } else if (isIntervalValue(newItem?.subOperator)) {
          newItem.dateValue = 0;
          newItem.value = '';
        }
      }
      /** 联系人参与度 */
      if (
        isSelectActivityOne(newItem.key) ||
        isSelectActivityTwo(newItem.key)
      ) {
        const options = filterJson
          ? getFiltersEnumOptions({
              filterJson,
              data: newItem,
              fieldKey: 'campaignTimeRange',
            })
          : getEnumOptions?.(newItem, 'campaignTimeRange');
        // 判断是否是 WhatsApp 活动
        const isWhatsAppCampaign =
          newItem.group === GroupTypeEnum.WHATSAPP_CAMPAIGN;
        const newCampaignScope: any = handleSubmitCampaignScope(
          options,
          newItem.campaignScope,
          isWhatsAppCampaign,
        );
        newItem.campaignScope = newCampaignScope;
      }
    }

    return newItem;
  });
};

// 偏好、标签、自定义字段、活动、关联信息被删除
export const isCitationDeletedKey = ({
  key,
  datasource,
  campaignScopeType,
  valueDeletedPair,
}: {
  datasource?: string;
  key?: string;
  campaignScopeType?: any;
  valueDeletedPair: any;
}):
  | 'isTagOrPreference'
  | 'isUserExtra'
  | 'isActivity'
  | 'isActivityAndJourney'
  | undefined => {
  if (!valueDeletedPair || isEmptyObject(valueDeletedPair)) {
    return;
  }

  if (datasource && ['user_preference', 'user_tag'].includes(datasource)) {
    return 'isTagOrPreference';
  }
  if (datasource && datasource === 'user_extra') {
    return 'isUserExtra';
  }
  if (
    key &&
    (isSelectActivityOne(key) || isSelectActivityTwo(key)) &&
    campaignScopeType === ContactEngagementRadioEnum.CAMPAIGNS
  ) {
    return 'isActivity';
  }
  if (
    key &&
    isSelectActivityAndJourney(key) &&
    campaignScopeType === ContentRatingTabEnum.CAMPAIGN
  ) {
    return 'isActivityAndJourney';
  }
  return;
};

// 引用被删除 - 自定义校验规则
export const onValidatorCitationDeleted = ({
  val,
  title = '',
  valueDeletedPair = {},
}: {
  val: any;
  title?: ConditionsListIF['title'];
  valueDeletedPair?: FilterFormType['valueDeletedPair'];
}) => {
  const deletedPairKeys: string[] = Object.keys(valueDeletedPair).map(String);
  const deletedPairValues: string[] = Object.values(valueDeletedPair);
  const deletedContentText = deletedPairValues.join();
  // 当前渲染项选中结果中存在被删除的关联项
  let hasDelValues: boolean = true;
  let type: 'error' | 'warning' | 'success' = 'success';
  let message: string = '';
  // 数组（多选之类）
  if (isArrayLen(val)) {
    hasDelValues = !val.some((key: string) =>
      deletedPairKeys.includes(String(key)),
    );
  }
  // 非数组（单选之类）
  if (!isArray(val)) {
    hasDelValues = !deletedPairKeys.includes(String(val));
  }
  if (!hasDelValues) {
    type = 'error';
    message = intlGet('SEGMENTS.MANAGE.CITATION.WAS.DELETED', {
      deletedContentText,
    });
  } else {
    type = 'success';
    message = '';
  }

  return {
    type,
    message,
    result: hasDelValues,
  };
};

// value剔除标签、偏好、活动被删除的关联信息
export const editStatusDelInfo = (newFilters: FilterFormType[]) => {
  const resultArr: any[] = [];
  for (let i = 0; i < newFilters.length; i++) {
    const item = newFilters[i];
    const newItem: FilterFormType = { ...item };
    const { datasource, valueDeletedPair = {}, key, campaignScope } = item;
    const campaignName = item?.campaignScope?.campaignName || [];
    const type = campaignScope?.type || 0;
    const deletedInfo = isCitationDeletedKey({
      datasource,
      key,
      campaignScopeType: type,
      valueDeletedPair,
    });
    if (deletedInfo === 'isTagOrPreference') {
      const newValue: any[] = [];
      const delValue = Object.keys(valueDeletedPair);
      item.value.map((value: string) => {
        !delValue.includes(value) && newValue.push(value);
      });
      newItem.value = newValue;
    }
    if (deletedInfo === 'isActivity' && item.campaignScope) {
      const newId: any[] = [];
      const delValue = Object.keys(valueDeletedPair);
      item.campaignScope?.campaignId?.map((value: number) => {
        !delValue.includes(String(value)) && newId.push(value);
      });

      const newName =
        (isArray(campaignName) &&
          campaignName?.filter((j) => !delValue.includes(String(j.value)))) ||
        [];
      if (newItem.campaignScope) {
        newItem.campaignScope.campaignId = newId;
        newItem.campaignScope.campaignName = newName;
      }
    }
    if (deletedInfo === 'isActivityAndJourney' && item.contentRating) {
      let newId: number = item.contentRating?.campaignId;
      const delValue = +Object.keys(valueDeletedPair)?.[0] || 0;
      if (item.contentRating?.campaignId === delValue) {
        newId = 0;
      }
      newItem.contentRating = {
        ...newItem.contentRating,
        campaignId: newId,
        campaignName: [],
      };
    }
    resultArr.push(newItem);
  }
  return resultArr;
};

// // 标签、偏好关联项被删除的下拉项信息重新写入下拉项内容中
// export const processCitationOptions = ({
//   valueDeletedPair,
//   options,
// }: {
//   valueDeletedPair: any;
//   options: FilterOptionType['filterOption'];
// }) => {
//   const newArr: any[] = [];
//   Object.keys(valueDeletedPair).map((i) => {
//     const name = valueDeletedPair[i];
//     const obj = {
//       group: '',
//       isBuiltin: false,
//       isShowJoinChannelBtn: false,
//       isShowJoinTimeBtn: false,
//       key: i,
//       text: { 'en-US': name, 'zh-CN': name },
//       type: '',
//       value: i,
//     };
//     const exists = options && options.some((v) => v.value === i);
//     !exists && newArr.unshift(obj);
//   });
//   return newArr;
// };

// 处理历史分层数据
export const processHistorySegment = ({
  filters,
  filterObj,
}: {
  filters: FormDataType['filters'];
  filterObj: any;
}) => {
  for (let i = 0; i < filters.length; i++) {
    const filtersItem = filters[i];
    // DropDown类型自定义字段、偏好历史数据 - 将选中的空值写入回显下拉项中
    if (isDropDownExtra(filtersItem) && !!filtersItem.isIncludeNullValue) {
      const isIncludeNullValueItem =
        filterObj[filtersItem.key]?.filterOption?.[0] || {};
      // 将选中包含空的值写入回显下拉项
      if (
        isArray(filtersItem.value) &&
        !filtersItem.value?.includes(isIncludeNullValueItem.value)
      ) {
        filtersItem.value.unshift(isIncludeNullValueItem.value);
      }
      // 将选中包含空的值写入回显下拉项
      if (isString(filtersItem.value)) {
        const values = filtersItem.value?.split(',');
        const unHasEmpty = !values?.includes(isIncludeNullValueItem.value);
        if (unHasEmpty) {
          values.unshift(isIncludeNullValueItem.value);
          filtersItem.value = values.join();
        }
      }
    }
  }
  return filters;
};

// 分层条件配置：处理获取的分层条件配置
export const processFilterConfig = (
  list: ConditionsListIF[],
  segmentTips: string | ReactNode,
) => {
  const localLanguage =
    getLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE) || DEFAULT_LANGUAGE;
  const filterList: FilterOptionType[] = [];
  const filterObj: any = {};
  list.forEach((item: ConditionsListIF) => {
    let disabled = false;
    let tips: ReactNode | undefined | string = '';
    // 当分组是segment时
    if (item.group === GroupTypeEnum.SEGMENT) {
      disabled = !item.isValid;
      tips = disabled ? segmentTips : '';
    }
    // 新增条件使用的
    filterList.push({
      label: getEnumLangValue(item.fieldLabel, localLanguage),
      value: item.key,
      disabled,
      tips,
      tooltipProps: {
        showArrow: true,
        overlayInnerStyle: {
          width: 340,
        },
        placement: 'right-top',
      },
      ...item,
    });

    // 新增后，各个选项依据key来使用的
    filterObj[item.key] = {
      ...item,
      title: getEnumLangValue(item.fieldLabel, localLanguage),
    };
  });
  return {
    filterList,
    filterObj,
  };
};

// 获取分层条件配置
export const getFilterConfig = async ({
  isNewVersionSegment,
  segmentTips,
  setLoading,
  setFilterOption,
  setFilterJson,
}: {
  isNewVersionSegment?: boolean;
  segmentTips: string | ReactNode;
  setLoading?: (val: boolean) => void;
  setFilterOption?: (val: any) => void;
  setFilterJson?: (val: any) => void;
}): Promise<{
  filterList: FilterOptionType[];
  filterObj: any;
}> => {
  const filterConfigData: {
    filterList: FilterOptionType[];
    filterObj: any;
  } = {
    filterList: [],
    filterObj: {},
  };
  try {
    const result = await segmentsManageApi
      .getFilterConfig({
        version: isNewVersionSegment ? 'v2.0' : 'v1.0',
      })
      .finally(() => {
        setLoading?.(false);
      });

    await handleReturnResults({
      params: result,
      successCallBack: (res) => {
        const { list = [] } = res?.data;
        const { filterList, filterObj } = processFilterConfig(
          list,
          segmentTips,
        );
        filterConfigData.filterList = filterList;
        filterConfigData.filterObj = filterObj;
        setFilterOption?.(filterList);
        setFilterJson?.(filterObj);
      },
    });
  } catch {
    setLoading?.(false);
  }
  return new Promise((resolve) => resolve(filterConfigData));
};

// 分层条件配置：处理被删除的配置项
export const processDeletedConfig = (params: {
  filters: FilterFormType[];
  filterList: any;
  filterObj: any;
  segmentTips: string | ReactNode;
  setFilterOption?: (val: any) => void;
  setFilterJson?: (val: any) => void;
}) => {
  const { filters, setFilterOption, setFilterJson } = params;
  let newFilterList: FilterOptionType[] = [...params.filterList];
  let newFilterObj: any = { ...params.filterObj };
  for (let i = 0; i < filters.length; i++) {
    const filtersItem = filters[i];
    // 获取当前被从配置中移除项信息json串
    let item = filtersItem?.ruleDetail ? jsonParse(filtersItem.ruleDetail) : {};
    // 将获取的json结果协议由下划线协议转为驼峰
    item = convertKeysToUnderscore({
      data: item,
      switchToUnderline: false,
    });
    // 将被删除的自定义字段关联配置重新写入当前渲染配置项中
    if (!isEmptyObject(item)) {
      const { filterList, filterObj } = processFilterConfig(
        [item],
        params.segmentTips,
      );
      newFilterList = [...newFilterList, ...filterList];
      newFilterObj = { ...newFilterObj, ...filterObj };
    }

    // // 还原偏好或标签被移除的下拉项信息
    // const { datasource, valueDeletedPair, key } = filtersItem;
    // 获取处理偏好或标签被移除的下拉项信息标识
    // const isPrefOrTag =
    //   isCitationDeletedKey({ datasource }) === 'isTagOrPreference';
    // if (isPrefOrTag) {
    //   const index = newFilterList.findIndex(
    //     (v) => v.datasource === datasource,
    //   );
    //   const options: FilterOptionType['filterOption'] =
    //     newFilterObj[key].filterOption || [];
    //   const newOptions = processCitationOptions({
    //     options,
    //     valueDeletedPair,
    //   });
    //   newFilterList[index].filterOption = [...newOptions, ...options];
    //   newFilterObj[key].filterOption = [...newOptions, ...options];
    // }
  }
  setFilterOption?.(newFilterList);
  setFilterJson?.(newFilterObj);
};
